import { createNoise2D } from 'simplex-noise';
import { BIOMES, getSystemSeed, ISLAND_RADIUS, MAP_RADIUS } from './TerrainConstants.js';

/**
 * Seeded PRNG using the Alea algorithm
 * @param {number} seed - The seed value
 * @returns {function} A seeded random number generator
 */
function createSeededRandom(seed) {
	let s0 = seed;
	let s1 = seed * 0xDEADBEEF;
	let s2 = seed * 0xBADF00D;
	
	return function() {
		s0 = s1;
		s1 = s2;
		s2 = s0 ^ s1 ^ s2;
		return (s2 >>> 0) / 0xFFFFFFFF;
	};
}

/**
 * Handles biome noise generation and classification
 * Responsible for:
 * - Generating noise maps for biome classification
 * - Converting noise values to biome types
 * - Providing utility functions for biome-specific noise
 */
export class BiomeGenerator {
	constructor(seed) {
		this.seed = seed || Math.floor(Math.random() * 1000000);
		this.noise2D = createNoise2D();
		this.random = createSeededRandom(this.seed);
		console.log(`[BiomeGenerator] Initialized with seed: ${this.seed}`);
		
		// Define biome types
		this.BIOMES = BIOMES;
		
		this.validate();
	}
	
	validate() {
		console.log('[BiomeGenerator] Running validation test...');
		const testPoints = [
			{ x: 0, z: 0 },
			{ x: MAP_RADIUS, z: MAP_RADIUS },
			{ x: -MAP_RADIUS, z: -MAP_RADIUS }
		];
		
		testPoints.forEach(point => {
			const biomeData = this.getBiomeData(point.x, point.z);
			console.log(`[BiomeGenerator] Point (${point.x}, ${point.z}):`, biomeData);
		});
	}
	
	getBiomeData(x, z) {
		const frequency = 0.02;
		const temperatureNoise = this.noise2D(x * frequency, z * frequency);
		const moistureNoise = this.noise2D((x + 1000) * frequency, (z + 1000) * frequency);
		const featureNoise = this.noise2D((x + 2000) * frequency, (z + 2000) * frequency);
		
		// Normalize noise values to 0-1 range
		const temperature = (temperatureNoise + 1) * 0.5;
		const moisture = (moistureNoise + 1) * 0.5;
		
		// Calculate distance from center
		const distanceFromCenter = Math.sqrt(x * x + z * z) / ISLAND_RADIUS;
		
		// Determine biome based on distance and conditions
		let biome;
		if (distanceFromCenter > 1.6) {
			biome = BIOMES.DEEP_OCEAN;
		} else if (distanceFromCenter > 1.0) {
			biome = BIOMES.OCEAN;
		} else if (distanceFromCenter > 0.85) {
			biome = BIOMES.SHORE;
		} else if (distanceFromCenter > 0.7) {
			biome = BIOMES.PLAINS;
		} else {
			// Inner area is a core biome - use noise to determine which one
			const coreNoise = (featureNoise + 1) * 0.5; // Normalize to 0-1
			
			// Use weighted noise for core biome selection
			if (coreNoise < 0.4) {
				biome = BIOMES.PLAINS; // 40% chance
			} else if (coreNoise < 0.7) {
				biome = BIOMES.HILLS; // 30% chance
			} else {
				biome = BIOMES.MOUNTAINS; // 30% chance
			}
		}
		
		return {
			biome,
			temperature,
			moisture,
			featureNoise,
			distanceFromCenter
		};
	}
	
	getBiome(x, z) {
		return this.getBiomeData(x, z).biome;
	}
}
